import React from 'react'
import { useI18n } from '../../i18n'
import { normalizeAssetType, isScannableAssetType, assetTypeI18nKey } from '../../constants/assetTaxonomy'
import type { AssetOperationalDetail } from '../../services/assetOperationalApi'
import styles from '../../styles/components/assets/AssetsInventoryIntelligence.module.scss'

interface Props {
  detail: AssetOperationalDetail
}

export default function AssetTypeOverviewSection({ detail }: Props) {
  const { t } = useI18n()
  const type = normalizeAssetType(detail.type)
  const scannable = isScannableAssetType(detail.type)

  const fields: Array<{ label: string; value: React.ReactNode }> = [
    { label: t('assets.assetPassport.type', 'Type'), value: t(assetTypeI18nKey(type), type.replace(/_/g, ' ')) },
    { label: t('assets.assetPassport.environment', 'Environment'), value: detail.environment || '—' },
    { label: t('assets.assetPassport.criticality', 'Criticality'), value: detail.criticality || '—' },
    { label: t('assets.assetPassport.owner', 'Owner'), value: detail.ownerLabel || t('assets.assetPassport.ownerMissing', 'Not assigned') },
  ]

  if (type === 'SERVER' || type === 'VIRTUAL_MACHINE' || type === 'CONTAINER') {
    fields.push(
      { label: t('assets.assetPassport.scanCapability', 'Scan'), value: scannable ? t('assets.inventory.scannable', 'Scannable') : t('assets.inventory.notScannable', 'Not scannable') },
      { label: t('assets.assetPassport.vulnCount', 'Vulnerabilities'), value: String(detail.vulnerabilityCount) },
      { label: t('assets.assetPassport.criticalVulns', 'Critical / high'), value: String(detail.criticalVulnerabilityCount) }
    )
  }

  if (type === 'DATABASE' || type === 'DATA_STORE' || type === 'FILE_SHARE') {
    fields.push(
      {
        label: t('assets.assetPassport.dataCategories', 'Data categories'),
        value: detail.dataCategories?.length
          ? detail.dataCategories.join(', ')
          : t('assets.assetPassport.noDataCategories', 'None via linked systems'),
      },
      { label: t('assets.assetPassport.linkedSystems', 'Linked systems'), value: String(detail.linkedSystemsCount) }
    )
  }

  if (type === 'SECURITY_TOOL' || type === 'SIEM' || type === 'EDR' || type === 'WAF') {
    fields.push(
      { label: t('assets.assetPassport.failedControls', 'Failed controls'), value: String(detail.failedControlsCount) },
      { label: t('assets.assetPassport.evidenceGaps', 'Evidence gaps'), value: String(detail.evidenceGapCount ?? 0) }
    )
  }

  if (type === 'CRYPTO_TOOL' || type === 'HSM' || type === 'CERTIFICATE' || type === 'KEY_MANAGEMENT_SYSTEM') {
    fields.push(
      { label: t('assets.assetPassport.openRisks', 'Open risks'), value: String(detail.openRisksCount) },
      { label: t('assets.assetPassport.remediationAging', 'Aging remediation'), value: String(detail.remediationAgingCount ?? 0) }
    )
  }

  if (type === 'CLOUD_RESOURCE' || type === 'CLOUD_ACCOUNT' || type === 'STORAGE_BUCKET') {
    fields.push(
      { label: t('assets.assetPassport.exposure', 'Exposure indicators'), value: (detail.exposureIndicators || []).join(', ') || '—' },
      { label: t('assets.assetPassport.linkedSystems', 'Linked systems'), value: String(detail.linkedSystemsCount) }
    )
  }

  return (
    <div className={styles.overviewGrid}>
      {fields.map((f) => (
        <div key={f.label} className={styles.overviewField}>
          <span className={styles.overviewLabel}>{f.label}</span>
          <span className={styles.overviewValue}>{f.value}</span>
        </div>
      ))}
      {(detail.exposureIndicators || []).length > 0 ? (
        <div className={styles.exposureChips}>
          {detail.exposureIndicators.map((ind) => (
            <span key={ind} className={styles.exposureChip}>
              {t(`assets.assetPosture.indicator.${ind}`, ind.replace(/_/g, ' '))}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  )
}
